import React, { useEffect, useMemo, useState } from "react";
import { getRutina, postRutina } from "../../../Service/Service.rutina";
import HCModal from "../HCModal";
import { Autocomplete, Field, Section } from "../HCPiezas";
import { hcError, hcExito } from "../hcSwal";

/* ============================================================================
   Rutinas de laboratorio — cargar una, o guardar la selección como una nueva.

   Sustituye a los dos Offcanvas (`Rutina.js` / `RutinaBuscar.js`), que eran
   otro lenguaje visual dentro del mismo flujo. Endpoints idénticos:
   GET listar/rutina y POST create/rutina { rutina, tipoExamen: [ids] }.

   Se abre ENCIMA del modal de laboratorio; la pila de `useDialogo` se encarga
   de que Escape cierre solo este y el Tab no se escape al de abajo.
   ========================================================================== */

export default function ModalRutinas({ modo, isOpen, onClose, onCargar, examenesSeleccionados = [] }) {
  const [rutinas, setRutinas] = useState([]);
  const [elegida, setElegida] = useState(null);
  const [nombre, setNombre] = useState("");
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setElegida(null);
    setNombre("");
    if (modo !== "cargar") return;
    (async () => {
      const r = await getRutina();
      if (r && r.success) setRutinas(r.data || []);
    })();
  }, [isOpen, modo]);

  const buscar = (t) => {
    const q = t.toLowerCase();
    return rutinas
      .filter((x) => String(x.nombre || "").toLowerCase().includes(q))
      .slice(0, 30)
      .map((x) => ({ value: x.id_rutina, label: x.nombre, __row: x }));
  };

  const detalles = useMemo(() => {
    const row = elegida?.__row;
    return (row?.detalles || []).map((d) => d.ExamenTipo).filter(Boolean);
  }, [elegida]);

  const cargar = () => {
    if (!elegida?.__row) return;
    onCargar(elegida.__row);
    onClose();
  };

  const guardar = async () => {
    if (!nombre.trim() || examenesSeleccionados.length === 0) return;
    setGuardando(true);
    const r = await postRutina({
      rutina: nombre.trim(),
      tipoExamen: examenesSeleccionados.map((x) => x.IdExamenTipo),
    });
    setGuardando(false);
    if (!r || r.success === false) {
      hcError("No se pudo guardar la rutina", "Intenta de nuevo.");
      return;
    }
    hcExito("Rutina guardada", `«${nombre.trim()}» con ${examenesSeleccionados.length} examen(es).`);
    onClose();
  };

  if (modo === "cargar") {
    return (
      <HCModal
        isOpen={isOpen}
        size="md"
        title="Usar una rutina"
        subtitle="Carga de una vez todos los exámenes de una rutina guardada."
        validation={{ missing: elegida ? [] : ["Rutina"] }}
        primaryLabel={detalles.length ? `Cargar ${detalles.length} examen(es)` : "Cargar"}
        onPrimary={cargar}
        onClose={onClose}
        onCancel={onClose}
      >
        <Section title="Rutina">
          <Field label="Buscar rutina" htmlFor="hc-rut-buscar">
            <Autocomplete
              id="hc-rut-buscar"
              valor={elegida}
              onElegir={setElegida}
              onBuscar={buscar}
              minimo={1}
              placeholder="Nombre de la rutina…"
              vacio="No hay rutinas con ese nombre"
            />
          </Field>

          {elegida && (
            <>
              <div className="hcf__label">Exámenes que incluye · {detalles.length}</div>
              <div className="hcya" style={{ maxHeight: 220, overflowY: "auto" }}>
                {detalles.length === 0 ? (
                  <div className="hclist__vacio">Esta rutina no tiene exámenes.</div>
                ) : (
                  detalles.map((e, i) => (
                    <div className="hcya__fila" key={e.IdExamenTipo ?? i}>
                      <span className="hcya__nom">{e.descripcion}</span>
                    </div>
                  ))
                )}
              </div>
              <div className="hcf__hint" style={{ marginTop: 6 }}>
                Cargar una rutina reemplaza la selección actual de exámenes.
              </div>
            </>
          )}
        </Section>
      </HCModal>
    );
  }

  return (
    <HCModal
      isOpen={isOpen}
      size="md"
      title="Guardar como rutina"
      subtitle="Para volver a pedir este mismo conjunto en un clic."
      validation={{ missing: nombre.trim() ? [] : ["Nombre"] }}
      primaryLabel="Guardar rutina"
      onPrimary={guardar}
      primaryLoading={guardando}
      onClose={onClose}
      onCancel={onClose}
      hayCambiosSinGuardar={Boolean(nombre.trim())}
    >
      <Section title="Nueva rutina">
        <Field label="Nombre de la rutina" required htmlFor="hc-rut-nombre">
          <input
            id="hc-rut-nombre"
            type="text"
            placeholder="Perfil prenatal, control de diabético…"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
          />
        </Field>

        <div className="hcf__label">Exámenes que se guardan · {examenesSeleccionados.length}</div>
        <div className="hcya" style={{ maxHeight: 220, overflowY: "auto" }}>
          {examenesSeleccionados.length === 0 ? (
            <div className="hclist__vacio">Marca primero los exámenes en la orden.</div>
          ) : (
            examenesSeleccionados.map((e) => (
              <div className="hcya__fila" key={e.IdExamenTipo}>
                <span className="hcya__nom">{e.descripcion}</span>
              </div>
            ))
          )}
        </div>
      </Section>
    </HCModal>
  );
}
